import { useAuth } from "../contexts/AuthContext";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import { Avatar } from "../design/avatar";
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from "../design/dropdown";
import {
  Navbar,
  NavbarItem,
  NavbarSection,
  NavbarSpacer,
} from "../design/navbar";
import {
  Sidebar,
  SidebarBody,
  SidebarFooter,
  SidebarItem,
  SidebarLabel,
  SidebarSection,
  SidebarSpacer,
} from "../design/sidebar";
import { SidebarLayout } from "../design/sidebar-layout";
import {
  ArrowRightStartOnRectangleIcon,
  ChevronUpIcon,
  LightBulbIcon,
  ShieldCheckIcon,
  UserCircleIcon,
  HomeIcon,
  QuestionMarkCircleIcon,
  DocumentIcon,
  DocumentCurrencyDollarIcon,
  ChartBarIcon,
  SparklesIcon,
  MagnifyingGlassIcon,
  ChartBarSquareIcon,
  BookOpenIcon,
  BuildingLibraryIcon,
} from "@heroicons/react/20/solid";
import { KeyIcon } from "@heroicons/react/16/solid";
import { useUser } from "../contexts/UserContext";
import { Link } from "../design/link";

function AccountDropdownMenu({
  anchor,
}: {
  anchor: "top start" | "bottom end";
}) {
  return (
    <DropdownMenu className="min-w-64" anchor={anchor}>
      <DropdownItem href="/settings">
        <UserCircleIcon />
        <DropdownLabel>My account</DropdownLabel>
      </DropdownItem>
      <DropdownDivider />
      <DropdownItem href="https://www.artcollection.io/privacy">
        <ShieldCheckIcon />
        <DropdownLabel>Privacy policy</DropdownLabel>
      </DropdownItem>
      <DropdownItem href="https://www.artcollection.io/terms">
        <DocumentIcon />
        <DropdownLabel>Terms of Service</DropdownLabel>
      </DropdownItem>
      <DropdownItem href="mailto:info@artcollection.io">
        <LightBulbIcon />
        <DropdownLabel>Share feedback</DropdownLabel>
      </DropdownItem>
      <DropdownDivider />
      <DropdownItem
        onClick={async () => {
          await signOut(auth);
        }}
      >
        <ArrowRightStartOnRectangleIcon />
        <DropdownLabel>Sign out</DropdownLabel>
      </DropdownItem>
    </DropdownMenu>
  );
}

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useUser();
  return (
    <SidebarLayout
      navbar={
        <Navbar>
          <NavbarSpacer />
          <NavbarSection>
            <Dropdown>
              <DropdownButton as={NavbarItem}>
                <Avatar square />
              </DropdownButton>
              <AccountDropdownMenu anchor="bottom end" />
            </Dropdown>
          </NavbarSection>
        </Navbar>
      }
      sidebar={
        <Sidebar>
          <SidebarBody>
            <SidebarSection>
              <SidebarItem href="/">
                <HomeIcon />
                <SidebarLabel>Dashboard</SidebarLabel>
              </SidebarItem>
            </SidebarSection>

            <SidebarSection className="max-lg:hidden">
              <div className="px-3 py-1">
                <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                  Analytics
                </span>
              </div>
              <SidebarItem href="/valuation">
                <SparklesIcon />
                <SidebarLabel>AI Valuation</SidebarLabel>
              </SidebarItem>
              <SidebarItem href="/auctionRecords">
                <MagnifyingGlassIcon />
                <SidebarLabel>Price Database</SidebarLabel>
              </SidebarItem>
              <SidebarItem href="/market">
                <ChartBarSquareIcon />
                <SidebarLabel>Market Trends</SidebarLabel>
              </SidebarItem>
              <SidebarItem href="/auction-houses">
                <BuildingLibraryIcon />
                <SidebarLabel>Auction House Performance</SidebarLabel>
              </SidebarItem>
            </SidebarSection>

            <SidebarSection className="max-lg:hidden">
              <div className="px-3 py-1">
                <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                  Developer
                </span>
              </div>
              <SidebarItem href="/keys">
                <KeyIcon />
                <SidebarLabel>API Keys</SidebarLabel>
              </SidebarItem>
              <SidebarItem href="/usages">
                <ChartBarIcon />
                <SidebarLabel>Usage</SidebarLabel>
              </SidebarItem>
              <SidebarItem href="/docs">
                <BookOpenIcon />
                <SidebarLabel>Documentation</SidebarLabel>
              </SidebarItem>
            </SidebarSection>

            <SidebarSpacer />

            <SidebarSection>
              <SidebarItem href="/subscriptions">
                <DocumentCurrencyDollarIcon />
                <SidebarLabel>Subscriptions</SidebarLabel>
              </SidebarItem>
              <SidebarItem href={`mailto:info@artcollection.io`}>
                <QuestionMarkCircleIcon />
                <SidebarLabel>Support</SidebarLabel>
              </SidebarItem>
            </SidebarSection>
          </SidebarBody>

          <SidebarFooter>
            <Dropdown>
              <DropdownButton as={SidebarItem}>
                <span className="flex items-center min-w-0 gap-3">
                  <Avatar
                    src={user?.avatarUrl}
                    className="size-10"
                    square
                    alt=""
                  />
                  <span className="min-w-0">
                    <span className="block font-medium truncate text-sm/5 text-zinc-950 dark:text-white">
                      {user?.firstName}
                    </span>
                    <span className="block font-normal truncate text-xs/5 text-zinc-500 dark:text-zinc-400">
                      {user?.email}
                    </span>
                  </span>
                </span>
                <ChevronUpIcon />
              </DropdownButton>
              <AccountDropdownMenu anchor="top start" />
            </Dropdown>
          </SidebarFooter>
        </Sidebar>
      }
    >
      {user && typeof user.freeCredits === "number" && (
        <div className={`border-b ${user.freeCredits > 0 ? "bg-gallery-50 border-gallery-200" : "bg-rose-50 border-rose-200"}`}>
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${user.freeCredits > 0 ? "bg-gallery-200" : "bg-rose-200"}`}>
                <SparklesIcon className={`w-4 h-4 ${user.freeCredits > 0 ? "text-gallery-600" : "text-rose-600"}`} />
              </div>
              <div>
                <p className={`text-sm font-medium ${user.freeCredits > 0 ? "text-gallery-900" : "text-rose-900"}`}>
                  {user.freeCredits > 0
                    ? `${user.freeCredits} free credit${user.freeCredits !== 1 ? "s" : ""} remaining`
                    : "No free credits remaining"}
                </p>
                <p className={`text-xs ${user.freeCredits > 0 ? "text-gallery-500" : "text-rose-600"}`}>
                  {user.freeCredits > 0
                    ? "Upgrade to unlock unlimited AI valuations"
                    : "Subscribe now to continue using AI valuations"}
                </p>
              </div>
            </div>
            <Link
              href="/subscriptions"
              className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                user.freeCredits > 0
                  ? "bg-gallery-900 text-white hover:bg-gallery-800"
                  : "bg-rose-600 text-white hover:bg-rose-700"
              }`}
            >
              {user.freeCredits > 0 ? "View Plans" : "Subscribe Now"}
              <svg className="w-4 h-4 ml-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      )}
      {children}
    </SidebarLayout>
  );
};

export default Layout;
